/**
 * Horizontal scroll carousel for mega-menu collection cards.
 * Uses event delegation so hydrated/deferred submenu content works.
 *
 * Important: do not use the native `disabled` attribute on nav buttons.
 * Disabling a focused button blurs it, which closes the mega menu via
 * header-menu's blur → deactivate handler.
 */
(function initMegaMenuCarousel() {
  const SCROLL_RATIO = 0.8;

  /**
   * @param {HTMLElement} root
   * @param {number} direction
   */
  function scrollTrack(root, direction) {
    const track = root.querySelector('[data-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    const amount = Math.max(track.clientWidth * SCROLL_RATIO, 220);
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  /**
   * @param {HTMLElement} button
   * @returns {boolean}
   */
  function isNavDisabled(button) {
    return button.getAttribute('aria-disabled') === 'true';
  }

  /**
   * @param {HTMLElement} root
   */
  function syncButtons(root) {
    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!(track instanceof HTMLElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= maxScroll - 2;
    const nextDisabled = atEnd || maxScroll <= 0;

    if (prev instanceof HTMLElement) {
      prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      prev.classList.toggle('is-disabled', atStart);
    }
    if (next instanceof HTMLElement) {
      next.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      next.classList.toggle('is-disabled', nextDisabled);
    }
  }

  /**
   * Keep focus on the menu trigger so blur does not close the mega menu.
   * @param {Event} event
   */
  function preventFocusSteal(event) {
    event.preventDefault();
  }

  /**
   * @param {HTMLElement} root
   * @param {number} direction
   * @param {Event} event
   */
  function onNavClick(root, direction, event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    if (!(button instanceof HTMLElement) || isNavDisabled(button)) return;

    scrollTrack(root, direction);
  }

  /**
   * @param {HTMLElement} root
   */
  function bindCarousel(root) {
    if (root.dataset.carouselBound === 'true') return;
    root.dataset.carouselBound = 'true';

    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');

    // Strip native disabled if present from older markup/hydration
    [prev, next].forEach((btn) => {
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = false;
        btn.addEventListener('mousedown', preventFocusSteal);
        btn.addEventListener('pointerdown', preventFocusSteal);
      }
    });

    prev?.addEventListener('click', (event) => onNavClick(root, -1, event));
    next?.addEventListener('click', (event) => onNavClick(root, 1, event));

    track?.addEventListener(
      'scroll',
      () => {
        syncButtons(root);
      },
      { passive: true }
    );

    const slides = root.querySelectorAll('.mega-menu-carousel__slide img');
    slides.forEach((img) => {
      if (img instanceof HTMLImageElement && !img.complete) {
        img.addEventListener('load', () => syncButtons(root), { once: true });
      }
    });

    syncButtons(root);
  }

  function scan() {
    document.querySelectorAll('[data-mega-menu-carousel]').forEach((node) => {
      if (node instanceof HTMLElement) bindCarousel(node);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = target.closest('[data-mega-menu-carousel]');
    if (root instanceof HTMLElement) bindCarousel(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  const observer = new MutationObserver(() => scan());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
